import React, { useState, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { agrogestao } from "@/api/agrogestaoClient"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Upload, Search, Check, AlertCircle, MapPin, Phone, Mail, Building, User, FileText, Download, Eye, Trash2 } from "lucide-react"
import { agrogestao as ag } from "@/api/agrogestaoClient"

const STORAGE_BUCKET = import.meta.env.VITE_SUPABASE_STORAGE_BUCKET || "uploads"

const tipos = [
  { value: "cliente", label: "Cliente" },
  { value: "fornecedor", label: "Fornecedor" },
  { value: "transportador", label: "Transportador" },
  { value: "outro", label: "Outro" }
]

export default function ParceiroForm({ parceiro, onSuccess, onCancel, initialTab = "geral" }) {
  const propriedadeId = localStorage.getItem("propriedade_selecionada")
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [file, setFile] = useState(null)
  const [activeTab, setActiveTab] = useState(initialTab)

  useEffect(() => {
    setActiveTab(initialTab)
  }, [initialTab])
  const [cnpjLoading, setCnpjLoading] = useState(false)
  const [cepLoading, setCepLoading] = useState(false)
  const [cnpjError, setCnpjError] = useState("")

  const { data: docsList = [], refetch: refetchDocsList } = useQuery({
    queryKey: ['parceiro_docs', parceiro?.id],
    queryFn: async () => {
      if (!parceiro?.id) return []
      const res = await agrogestao.entities.ParceiroDocumento.filter({ parceiro_id: parceiro.id })
      return res || []
    },
    enabled: !!parceiro?.id
  })

  const uploadDocMutation = useMutation({
    mutationFn: async (fileToUpload) => {
      const path = `parceiros/${propriedadeId}/${parceiro.id}/${Date.now()}_${fileToUpload.name}`
      const res = await agrogestao.integrations.Core.UploadPrivateFile({ file: fileToUpload, path })

      await agrogestao.entities.ParceiroDocumento.create({
        parceiro_id: parceiro.id,
        propriedade_id: propriedadeId,
        nome: res.name || fileToUpload.name,
        url: res.path,
        tipo: fileToUpload.type,
        tamanho: fileToUpload.size,
        created_by: (await agrogestao.auth.me())?.email
      })
    },
    onSuccess: () => {
      refetchDocsList()
    }
  })

  const deleteDocMutation = useMutation({
    mutationFn: async (docId) => {
      await agrogestao.entities.ParceiroDocumento.delete(docId)
    },
    onSuccess: () => refetchDocsList()
  })

  const handleDownloadDoc = async (docUrl) => {
    try {
      if (!docUrl) return
      const raw = String(docUrl)
      if (raw.includes("token=") || raw.includes("/storage/v1/object/sign/")) {
        window.open(raw, "_blank")
        return
      }
      let bucket = STORAGE_BUCKET
      let path = raw
      if (raw.startsWith("http://") || raw.startsWith("https://") || raw.includes("storage/v1/object/")) {
        const full = raw.startsWith("http") ? raw : `https://${raw}`
        const url = new URL(full)
        const marker = "/storage/v1/object/"
        const idx = url.pathname.indexOf(marker)
        if (idx >= 0) {
          const rest = url.pathname.slice(idx + marker.length).split("/").filter(Boolean)
          if (rest[0] === "public" || rest[0] === "authenticated" || rest[0] === "sign") {
            rest.shift()
          }
          bucket = rest.shift() || bucket
          path = rest.join("/")
        } else {
          path = url.pathname.replace(/^\/+/, "")
        }
      }
      const { signed_url } = await agrogestao.integrations.Core.CreateFileSignedUrl({ bucket, path })
      if (signed_url) window.open(signed_url, "_blank")
    } catch (e) {
      alert("Erro ao abrir documento")
    }
  }

  const [formData, setFormData] = useState({
    tipo: "cliente",
    cnpj_cpf: "",
    nome_razao: "",
    telefone: "",
    email: "",
    endereco: "",
    numero: "",
    bairro: "",
    cidade: "",
    estado: "",
    cep: "",
    modal_transporte: "",
    status: "ativo",
    observacoes: "",
    atividade_principal: ""
  })

  useEffect(() => {
    if (parceiro) {
      setFormData({
        tipo: parceiro.tipo || "cliente",
        cnpj_cpf: parceiro.cnpj_cpf || "",
        nome_razao: parceiro.nome_razao || parceiro.razao_social || parceiro.nome || "",
        telefone: parceiro.telefone || "",
        email: parceiro.email || "",
        endereco: parceiro.endereco || "",
        numero: parceiro.numero || "",
        bairro: parceiro.bairro || "",
        cidade: parceiro.cidade || "",
        estado: parceiro.estado || "",
        cep: parceiro.cep || "",
        modal_transporte: parceiro.modal_transporte || "",
        status: parceiro.status || "ativo",
        observacoes: parceiro.observacoes || "",
        atividade_principal: parceiro.atividade_principal || "",
        documento_url: parceiro.documento_url || null
      })
    }
  }, [parceiro])

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const u = await agrogestao.auth.me()
      // Concatenate address fields if needed or store separately. 
      // Existing schema likely uses a single 'endereco' string or partials.
      // We will try to save detailed fields if the schema supports it, 
      // but fall back to concatenating into 'endereco' if needed to be safe.
      // However, the previous code read 'endereco', 'cidade', 'estado', 'cep'.
      // I'll assume 'numero' and 'bairro' might not be in the schema yet, so I'll append them to 'endereco' if not present in schema.
      // But for better UX, I'll store them in 'endereco' as "Logradouro, Numero - Bairro" if the backend doesn't support them.
      // Let's assume standard behavior: update what we can.

      const fullAddress = data.endereco // Simplify: keep main address field as logradouro

      const payload = {
        ...data,
        endereco: fullAddress, // Ensure we send the main address field
        propriedade_id: propriedadeId,
        created_by: u?.email || null
      }

      let docUrl = null
      if (file) {
        const res = await agrogestao.integrations.Core.UploadPrivateFile({ file, path: `parceiros/${propriedadeId}/${Date.now()}_${file.name}` })
        docUrl = res?.file_url || null
      }
      if (docUrl) payload.documento_url = docUrl

      const row = await agrogestao.entities.Parceiro.create(payload)
      try {
        await agrogestao.access.logAudit({ propriedade_id: propriedadeId, acao: 'criar', table: 'parceiros', status: 'success', details: { id: row.id } })
      } catch { }
      return row
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["parceiros", propriedadeId] })
      onSuccess()
    }
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const payload = { ...data }
      let docUrl = null
      if (file) {
        const res = await agrogestao.integrations.Core.UploadPrivateFile({ file, path: `parceiros/${propriedadeId}/${Date.now()}_${file.name}` })
        docUrl = res?.file_url || null
      }
      if (docUrl) payload.documento_url = docUrl
      const before = parceiro || null
      const row = await agrogestao.entities.Parceiro.update(id, payload)
      try {
        await agrogestao.access.logAudit({ propriedade_id: propriedadeId, acao: 'atualizar', table: 'parceiros', status: 'success', details: { id, before, after: row } })
      } catch { }
      return row
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["parceiros", propriedadeId] })
      onSuccess()
    }
  })

  const formatDocumento = (v) => {
    const s = v.replace(/\D/g, "")
    if (s.length <= 11) { // CPF
      return s.replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    } else { // CNPJ
      return s.replace(/^(\d{2})(\d)/, "$1.$2")
        .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
        .replace(/\.(\d{3})(\d)/, ".$1/$2")
        .replace(/(\d{4})(\d)/, "$1-$2")
    }
  }

  const handleDocChange = (e) => {
    let v = e.target.value
    setFormData({ ...formData, cnpj_cpf: v })
  }

  const validarCNPJ = (v) => {
    const s = String(v || "").replace(/\D/g, "")
    if (s.length === 11) return true // Basic CPF length check
    if (s.length !== 14) return false
    if (/^(\d)\1+$/.test(s)) return false
    const calc = (base) => {
      let sum = 0
      let pos = base.length - 7
      for (let i = base.length; i >= 1; i--) {
        sum += parseInt(base[base.length - i], 10) * pos--
        if (pos < 2) pos = 9
      }
      const res = sum % 11
      return res < 2 ? 0 : 11 - res
    }
    const d1 = calc(s.substring(0, 12))
    const d2 = calc(s.substring(0, 12) + d1)
    return s.endsWith(String(d1) + String(d2))
  }

  const consultarCNPJ = async () => {
    setCnpjError("")
    const doc = formData.cnpj_cpf
    if (!validarCNPJ(doc)) { setCnpjError("Documento inválido"); return }
    // Only fetch for CNPJ
    if (String(doc).replace(/\D/g, "").length !== 14) return

    setCnpjLoading(true)
    try {
      let data = null

      // Try BrasilAPI (CORS friendly, fast)
      try {
        const resp = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${String(doc).replace(/\D/g, "")}`)
        if (resp.ok) {
          const json = await resp.json()
          data = {
            nome: json.nome_fantasia || json.razao_social,
            razao_social: json.razao_social,
            logradouro: json.logradouro,
            numero: json.numero,
            bairro: json.bairro,
            municipio: json.municipio,
            uf: json.uf,
            cep: json.cep,
            email: json.email,
            telefone: json.ddd_telefone_1,
            atividade_principal: [{ text: json.cnae_fiscal_descricao }]
          }
        }
      } catch (e) {
        console.warn("BrasilAPI failed", e)
      }

      if (!data) { setCnpjError("CNPJ não encontrado ou erro de conexão"); return }

      const nome = data.nome || data.razao_social || ""
      const atividade = Array.isArray(data.atividade_principal) && data.atividade_principal.length ? (data.atividade_principal[0].text || data.atividade_principal[0].descricao || "") : ""

      setFormData(prev => ({
        ...prev,
        nome_razao: nome || prev.nome_razao,
        endereco: data.logradouro || prev.endereco,
        numero: data.numero || prev.numero,
        bairro: data.bairro || prev.bairro,
        cidade: data.municipio || (data.cidade) || prev.cidade,
        estado: data.uf || prev.estado,
        cep: String(data.cep || "").replace(/\D/g, "") || prev.cep,
        atividade_principal: atividade || prev.atividade_principal,
        email: data.email || prev.email,
        telefone: data.telefone || prev.telefone
      }))
      setActiveTab("endereco")
    } catch (e) {
      setCnpjError("Falha ao consultar CNPJ")
    } finally {
      setCnpjLoading(false)
    }
  }

  const buscarCEP = async () => {
    const cep = formData.cep.replace(/\D/g, "")
    if (cep.length !== 8) return
    setCepLoading(true)
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      const data = await resp.json()
      if (!data.erro) {
        setFormData(prev => ({
          ...prev,
          endereco: data.logradouro || prev.endereco,
          bairro: data.bairro || prev.bairro,
          cidade: data.localidade || prev.cidade,
          estado: data.uf || prev.estado
        }))
      }
    } catch {
      // ignore
    } finally {
      setCepLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.cnpj_cpf || !formData.nome_razao || !formData.tipo) {
      alert("Preencha os campos obrigatórios")
      return
    }
    if (String(formData.cnpj_cpf || "").replace(/\D/g, "").length === 14 && !validarCNPJ(formData.cnpj_cpf)) {
      setCnpjError("CNPJ inválido")
      return
    }

    setSaving(true)
    try {
      if (parceiro) {
        await updateMutation.mutateAsync({ id: parceiro.id, data: formData })
      } else {
        await createMutation.mutateAsync(formData)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-4 mb-4">
          <TabsTrigger value="geral">Dados Gerais</TabsTrigger>
          <TabsTrigger value="endereco">Endereço e Contato</TabsTrigger>
          <TabsTrigger value="outros">Observações</TabsTrigger>
          <TabsTrigger value="docs">Documentos</TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto px-1">
          <TabsContent value="geral" className="space-y-4 mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Parceiro *</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tipos.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>CPF / CNPJ *</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.cnpj_cpf}
                  onChange={handleDocChange}
                  placeholder="00.000.000/0000-00"
                  className={cnpjError ? "border-red-500" : ""}
                  required
                />
                <Button type="button" variant="outline" onClick={consultarCNPJ} disabled={cnpjLoading || formData.cnpj_cpf.replace(/\D/g, '').length !== 14}>
                  {cnpjLoading ? <div className="w-4 h-4 border-2 border-stone-400 border-t-stone-800 rounded-full animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {cnpjError && <span className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {cnpjError}</span>}
            </div>

            <div className="space-y-2">
              <Label>Razão Social / Nome Completo *</Label>
              <Input
                value={formData.nome_razao}
                onChange={(e) => setFormData({ ...formData, nome_razao: e.target.value })}
                className="font-medium"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Atividade Principal</Label>
              <Input value={formData.atividade_principal} onChange={(e) => setFormData({ ...formData, atividade_principal: e.target.value })} placeholder="Ex: Comércio varejista..." />
            </div>

            {formData.tipo === 'transportador' && (
              <div className="space-y-2">
                <Label>Modal de transporte</Label>
                <Select value={formData.modal_transporte} onValueChange={(v) => setFormData({ ...formData, modal_transporte: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rodoviario">Rodoviário</SelectItem>
                    <SelectItem value="ferroviario">Ferroviário</SelectItem>
                    <SelectItem value="hidroviario">Hidroviário</SelectItem>
                    <SelectItem value="aereo">Aéreo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </TabsContent>

          <TabsContent value="endereco" className="space-y-4 mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Phone className="w-3 h-3" /> Telefone / WhatsApp</Label>
                <Input value={formData.telefone} onChange={(e) => setFormData({ ...formData, telefone: e.target.value })} placeholder="(00) 00000-0000" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Mail className="w-3 h-3" /> Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@exemplo.com" />
              </div>
            </div>

            <div className="border-t border-stone-100 my-2 pt-2">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 col-span-1">
                  <Label>CEP</Label>
                  <div className="relative">
                    <Input
                      value={formData.cep}
                      onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                      onBlur={buscarCEP}
                      placeholder="00000-000"
                    />
                    {cepLoading && <div className="absolute right-2 top-2.5 w-4 h-4 border-2 border-stone-200 border-t-stone-500 rounded-full animate-spin" />}
                  </div>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Endereço</Label>
                  <Input value={formData.endereco} onChange={(e) => setFormData({ ...formData, endereco: e.target.value })} placeholder="Rua, Av..." />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="space-y-2 col-span-1">
                  <Label>Número</Label>
                  <Input value={formData.numero} onChange={(e) => setFormData({ ...formData, numero: e.target.value })} />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Bairro</Label>
                  <Input value={formData.bairro} onChange={(e) => setFormData({ ...formData, bairro: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Cidade</Label>
                  <Input value={formData.cidade} onChange={(e) => setFormData({ ...formData, cidade: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Estado (UF)</Label>
                  <Input value={formData.estado} onChange={(e) => setFormData({ ...formData, estado: e.target.value })} maxLength={2} />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="outros" className="space-y-4 mt-0">
            <div className="space-y-2">
              <Label>Observações Gerais</Label>
              <Textarea
                rows={12}
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Digite informações adicionais aqui..."
                className="resize-none"
              />
            </div>
          </TabsContent>

          <TabsContent value="docs" className="space-y-4 mt-0">
            {!parceiro?.id ? (
              <div className="flex flex-col items-center justify-center p-12 text-center border border-dashed rounded-lg bg-stone-50">
                <AlertCircle className="w-10 h-10 text-stone-300 mb-3" />
                <h3 className="text-lg font-medium text-stone-900">Salvar primeiro</h3>
                <p className="text-stone-500 max-w-xs mx-auto mt-1">É necessário salvar o cadastro do parceiro antes de anexar documentos.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {docsList.map(doc => (
                    <div key={doc.id} className="flex items-center gap-3 p-3 border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow group">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-900 truncate" title={doc.nome}>{doc.nome}</p>
                        <p className="text-xs text-stone-500 flex items-center gap-1">
                          {doc.tamanho ? `${(doc.tamanho / 1024).toFixed(1)} KB` : 'N/A'}
                          <span className="w-1 h-1 rounded-full bg-stone-300" />
                          {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-stone-500 hover:text-blue-600 hover:bg-blue-50"
                          onClick={() => handleDownloadDoc(doc.url)}
                          title="Visualizar/Baixar"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-stone-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => { if (confirm('Excluir documento permanentemente?')) deleteDocMutation.mutate(doc.id) }}
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {docsList.length === 0 && (
                    <div className="col-span-full py-8 text-center text-stone-500 italic">
                      Nenhum documento anexado.
                    </div>
                  )}
                </div>

                <div className="border-2 border-dashed border-stone-200 rounded-lg p-6 bg-stone-50/50 flex flex-col items-center gap-3 text-center transition-colors hover:bg-stone-50 hover:border-blue-200">
                  <Input
                    type="file"
                    id="doc-upload-new"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files?.[0]) uploadDocMutation.mutate(e.target.files[0])
                      e.target.value = null // reset input
                    }}
                  />
                  <Label htmlFor="doc-upload-new" className="cursor-pointer flex flex-col items-center gap-2 w-full">
                    <div className="w-12 h-12 rounded-full bg-white border shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                      {uploadDocMutation.isPending ? (
                        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Upload className="w-6 h-6 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-stone-900 block">
                        {uploadDocMutation.isPending ? "Enviando arquivo..." : "Clique para anexar um novo documento"}
                      </span>
                      <span className="text-xs text-stone-500 mt-1 block">Suporta PDF, Imagens, Word, Excel</span>
                    </div>
                  </Label>
                </div>
              </div>
            )}
          </TabsContent>
        </div>
      </Tabs>

      <div className="flex justify-end gap-2 pt-4 border-t border-stone-100 mt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" disabled={saving || createMutation.isPending || updateMutation.isPending} className="bg-[#1B4332] hover:bg-[#2D6A4F] min-w-[120px]">
          {saving ? 'Salvando...' : 'Salvar Cadastro'}
        </Button>
      </div>
    </form>
  )
}
